/* eslint no-underscore-dangle: ["error", { "allow": [ "_id"] }] */
import React, { useEffect, useState } from 'react';
import Box from '@mui/material/Box';
import { DataGrid, GridActionsCellItem, GridToolbarQuickFilter } from '@mui/x-data-grid';
import { Button, Chip, Stack, Typography } from '@mui/material';
import { Reply } from '@mui/icons-material';
import { useDispatch, useSelector } from 'react-redux';
import LoadingSkeleton from '../../utils/LoadingSkeleton';
import NoRowsOverlay from '../../utils/NoRowOverlay';
import UserAdd from './add';
import UserEdit from './edit';
import UserDelete from './delete';
import UserAction from '../../store/actions/UserAction';
import CopyInput from '../../components/CopyInput';

function QuickSearchToolbar() {
  const [addOpen, setAddOpen] = useState(false);

  return (
    <Box
      className="tableTopWrap page__title"
      sx={{
        borderBottom: '1px solid rgba(0,0,0,0.1)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        p: 3
      }}
    >
      <Stack direction="row" sx={{ ml: 'auto' }} spacing={2} className="MuiDataGrid-Search">
        <GridToolbarQuickFilter
          quickFilterParser={(searchInput) => searchInput.split(',').map((value) => value.trim())}
          quickFilterFormatter={(quickFilterValues) => quickFilterValues.join(', ')}
          debounceMs={800} // time before applying the new quick filter value
          variant="outlined"
          size="small"
          sx={{ pb: 0, maxWidth: '275px' }}
        />

        <Button
          className="button_primary"
          variant="contained"
          size="small"
          color="secondary"
          onClick={() => {
            setAddOpen(true);
          }}
        >
          Add User
        </Button>
      </Stack>
      {addOpen === true ? (
        <UserAdd openmodal={addOpen} onCloseModal={() => setAddOpen(false)} />
      ) : (
        ''
      )}
    </Box>
  );
}

export default function UserList() {
  const [paginationModel, setPaginationModel] = React.useState({
    page: 0,
    pageSize: 10
  });

  const [sortInfo, setSortInfo] = React.useState({
    sortField: '',
    sort: ''
  });
  const [searchKey, setSearchKey] = React.useState('');
  const [editOpen, setEditOpen] = React.useState(false);
  const [deleteOpen, setDeleteOpen] = React.useState(false);
  const [itemID, setItemID] = React.useState('');
  const [rowCountState, setRowCountState] = React.useState(0);
  const [data, setData] = React.useState([]);
  const dispatch = useDispatch();
  const { userData, isLoading } = useSelector((state) => state.user); // Replace 'data' with your reducer name
  const { paginationData, sortingData, keyword } = useSelector((state) => state.user.queryParams);

  useEffect(() => {
    dispatch(UserAction.updateQueryParams(paginationModel, sortInfo, searchKey));
    dispatch(UserAction.getAll(paginationModel, sortInfo, searchKey));
  }, [dispatch, paginationModel, sortInfo, searchKey]);

  useEffect(() => {
    if (userData) {
      if (userData.data) {
        setData(userData.data);
      } else {
        setData([]);
      }
      setRowCountState((prevRowCountState) =>
        userData.total !== undefined ? userData.total : prevRowCountState
      );
    }
  }, [userData]);

  useEffect(() => {
    if (paginationData) {
      setPaginationModel(paginationData);
    }
    if (sortingData) {
      setSortInfo(sortingData);
    }
    if (keyword) {
      setSearchKey(keyword);
    }
  }, [paginationData, keyword, sortingData]);

  const handleResendSetPasswordLink = (id) => {
    dispatch(UserAction.resendActivation(id));
  };
  const columns = [
    {
      field: 'unique_id',
      headerName: '',
      sortable: false,
      align: 'left',
      flex: 0.3,
      renderCell: (params) => {
        if (params && params.value) {
          return <CopyInput id={params.value} />;
        }
        return null;
      }
    },
    {
      field: 'name',
      headerName: 'Name',
      flex: 1
    },
    {
      field: 'email',
      headerName: 'Email ID',
      flex: 1
    },
    {
      field: 'department',
      headerName: 'Department',
      flex: 1
    },
    {
      field: 'role',
      headerName: 'Role',
      flex: 1
    },
    {
      field: 'status',
      headerName: 'Status',
      flex: 1,
      renderCell: (params) => {
        if (params && params.value) {
          if (params.value === 1) {
            return <Chip label="Active" color="success" />;
          }
          if (params.value === 2) {
            return <Chip label="Inactive" color="lignhtwarning" />;
          }
          return <Chip label={params?.row?.status_name} color="warning" />;
        }
        return null;
      }
    },
    {
      field: 'actions',
      type: 'actions',
      headerName: 'Actions',
      align: 'left',
      flex: 1,
      getActions: (params) => [
        params?.row?.status !== '14' ? (
          <GridActionsCellItem
            key={0}
            icon={<Reply />}
            className="actionBtn"
            title="Resend Activation"
            label="Resend Activation"
            onClick={() => {
              handleResendSetPasswordLink(params.id);
            }}
          />
        ) : (
          <span />
        ),
        <GridActionsCellItem
          icon={<i className="icon-edit" />}
          key={1}
          className="actionBtn"
          label="Edit"
          title="Edit"
          onClick={() => {
            setEditOpen(true);
            setItemID(params.id);
          }}
        />,
        <GridActionsCellItem
          icon={<i className="icon-delete" />}
          key={2}
          className="actionBtn"
          title="Delete"
          label="Delete"
          onClick={() => {
            setDeleteOpen(true);
            setItemID(params.id);
          }}
        />
      ]
    }
  ];

  const handleSortModelChange = React.useCallback((sortModel) => {
    // Here you save the data you need from the sort model
    if (sortModel.length > 0) {
      const { field, sort } = sortModel[0];
      const sortData = {
        sortField: field,
        sort
      };
      setSortInfo(sortData);
    }
    // else {
    //   dispatch(UserAction.getAll(paginationModel, sortInfo));
    // }
    // eslint-disable-next-line
  }, []);

  const onFilterChange = React.useCallback((filterModel) => {
    // Here you save the data you need from the filter model
    const searchKeyword = filterModel.quickFilterValues[0];
    setPaginationModel({ page: 0, pageSize: 10 });
    setSearchKey(searchKeyword);
    // dispatch(UserAction.getAll(paginationModel, sortInfo, keyword));
    // eslint-disable-next-line
  }, []);

  return (
    <Box sx={{ mb: 3 }} className="cardWrapper">
      <div className="pageTitleWrap">
        <Typography className="" component="h2" variant="h2">
          Users
        </Typography>
      </div>
      <Box sx={{ bgcolor: 'white' }} className="dataTable__wrap">
        <DataGrid
          rows={data}
          getRowId={(row) => row._id}
          rowCount={rowCountState}
          columns={columns}
          initialState={{
            pagination: {
              paginationModel: {
                pageSize: 10
              }
            }
          }}
          autoHeight
          sx={{ minHeight: 650, '--DataGrid-overlayHeight': '50vh' }}
          disableColumnFilter
          rowHeight={50}
          slots={{
            toolbar: QuickSearchToolbar,
            loadingOverlay: LoadingSkeleton,
            noRowsOverlay: NoRowsOverlay
          }}
          pageSizeOptions={[10, 25, 50, 100]}
          paginationModel={paginationModel}
          paginationMode="server"
          onPaginationModelChange={setPaginationModel}
          sortingMode="server"
          onSortModelChange={handleSortModelChange}
          filterMode="server"
          onFilterModelChange={onFilterChange}
          disableRowSelectionOnClick
          loading={isLoading}
        />
      </Box>

      {editOpen === true ? (
        <UserEdit openmodal={editOpen} itemId={itemID} onCloseModal={() => setEditOpen(false)} />
      ) : (
        ''
      )}
      {deleteOpen === true ? (
        <UserDelete
          openmodal={deleteOpen}
          itemId={itemID}
          onCloseModal={() => setDeleteOpen(false)}
        />
      ) : (
        ''
      )}
    </Box>
  );
}
